/**
 * Expanded drill-down for an OCF reconciliation alert card.
 *
 * Keeps the summary KPI grid from the alert payload, then lazily loads the
 * per-booking mismatch rows for the alert's handling agent from
 * `GET /v1/feed/{id}/ocf-reconciliation` and renders them grouped into three
 * buckets (OCF higher / No published rate / Out of booking policy). Each row
 * deep-links to the booking, the source ingest file (the Toptrans attachment
 * that reported the OCF), and the benchmark published rate line.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

import { useUserPreferences } from "@/context/UserPreferencesContext";
import { usePortNames } from "@/hooks/usePortNames";
import { extractLaneLocodes, formatLaneKey } from "@/lib/formatPort";
import { cn } from "@/lib/utils";
import { api } from "@/services/api";
import { formatUsd } from "@/types/booking";
import type {
  OcfReconciliationDrillResponse,
  OcfReconciliationDrillRow,
  OcfReconciliationGroups,
  ReportAlertPayload,
} from "@/types/feed";

import {
  persistRateLineFilters,
  rateLineFiltersFromLaneEquipment,
} from "@/features/rates/rateLineFilterLink";

import { FeedAttachmentDownloads } from "../../FeedAttachmentDownloads";
import { bookingHref, rateLineHref } from "../../feedLinks";
import { InsightShell, EntitySection, MetricGrid, type MetricItem } from "../primitives";
import { formatMetricValue, pickMetricLabel } from "../formatInsight";
import type { BatchAttachmentRef } from "@/types/feed";

/** Summary metrics shown above the grouped rows, in a stable order. */
const SUMMARY_KEYS = [
  "mismatch_count",
  "ocf_higher_count",
  "no_published_rate_count",
  "out_of_policy_count",
  "delta_tolerance_usd",
] as const;

const SECTIONS: { key: keyof OcfReconciliationGroups; title: string }[] = [
  { key: "ocf_higher", title: "OCF higher" },
  { key: "out_of_booking_policy", title: "Out of booking policy" },
  { key: "no_published_rate", title: "No published rate" },
];

function num(value: number | null | undefined): number | undefined {
  return value == null ? undefined : value;
}

/**
 * Collect the distinct source ingest files across every mismatch row. The whole
 * report for one agent is generated from the same agent sheet, so this usually
 * yields a single attachment surfaced once at the top rather than repeated on
 * every row.
 */
function collectAttachments(
  groups: OcfReconciliationGroups,
): BatchAttachmentRef[] {
  const seen = new Set<string>();
  const out: BatchAttachmentRef[] = [];
  const allRows = [
    ...groups.ocf_higher,
    ...groups.no_published_rate,
    ...groups.out_of_booking_policy,
  ];
  for (const row of allRows) {
    if (!row.source_batch_id || !row.source_attachment_id) continue;
    const key = `${row.source_batch_id}:${row.source_attachment_id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      batch_id: row.source_batch_id,
      attachment_id: row.source_attachment_id,
      filename: row.source_attachment_filename ?? "source file",
    });
  }
  return out;
}

/** One mismatch row: booking + lane + equipment, price comparison, links. */
function DrillRow({ row }: { row: OcfReconciliationDrillRow }) {
  const { preferences } = useUserPreferences();
  const laneKey = `${row.origin_port_code}→${row.destination_port_code}`;
  const portNames = usePortNames(extractLaneLocodes(laneKey));
  const laneLabel = formatLaneKey(laneKey, preferences.showLocodes, portNames);
  const equipment = [row.equipment_size, row.equipment_type]
    .filter((v): v is string => !!v && v.trim().length > 0)
    .join(" ");

  const delta = num(row.frt_delta);
  const hasPublished = row.published_frt != null;
  const excessEstimate = num(row.policy_excess_estimate_usd);

  return (
    <li className="space-y-1.5 rounded-lg border border-white/[0.08] bg-white/[0.05] px-2.5 py-2">
      <div className="flex items-center justify-between gap-2">
        <Link
          to={bookingHref(row.source_id)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex min-w-0 items-center gap-0.5 truncate text-label font-medium text-primary hover:underline"
        >
          <span className="truncate">{row.source_id}</span>
          <ArrowUpRight className="size-2.5 shrink-0" />
        </Link>
        <span className="shrink-0 truncate text-[10px] text-muted-foreground">
          {laneLabel}
          {equipment ? ` · ${equipment}` : ""}
        </span>
      </div>

      <div className="flex items-center justify-between gap-2 text-[11px]">
        <span className="min-w-0 truncate text-muted-foreground">
          OCF {formatUsd(num(row.ocf_frt))}
          {hasPublished && (
            <>
              {" vs "}
              {formatUsd(num(row.published_frt))}
              {row.published_rate_expired && (
                <span className="text-muted-foreground/70"> (expired)</span>
              )}
            </>
          )}
          {row.pricing_date && (
            <span className="text-muted-foreground/70">
              {" · PCD "}
              {row.pricing_date}
            </span>
          )}
        </span>
        {delta != null ? (
          <span
            className={cn(
              "shrink-0 font-semibold tabular-nums",
              delta > 0 ? "text-red-400" : "text-emerald-400",
            )}
          >
            {delta > 0 ? "+" : ""}
            {formatUsd(delta)}
          </span>
        ) : excessEstimate != null ? (
          <span className="shrink-0 font-semibold tabular-nums text-amber-400">
            est. +{formatUsd(excessEstimate)}
            {row.policy_excess_containers != null && (
              <span className="font-normal text-muted-foreground">
                {" "}
                ({row.policy_excess_containers} over max)
              </span>
            )}
          </span>
        ) : null}
      </div>

      {row.rate_card_id && (
        <div className="flex flex-wrap items-center gap-1">
          <Link
            to={rateLineHref(row.rate_card_id, row.rate_line_id)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.stopPropagation();
              // Pre-filter the card's lines to this lane + equipment so the
              // benchmark line is isolated for a quick side-by-side.
              persistRateLineFilters(
                rateLineFiltersFromLaneEquipment({
                  originPort: row.origin_port_code,
                  destinationPort: row.destination_port_code,
                  equipmentSize: row.equipment_size,
                  equipmentType: row.equipment_type,
                }),
              );
            }}
            className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.06] px-3 py-1 text-xs font-medium text-foreground/80 transition-colors hover:bg-white/[0.12]"
          >
            View published rate
            <ArrowUpRight className="size-3" />
          </Link>
        </div>
      )}
    </li>
  );
}

export function OcfReconciliationAlertView({
  eventId,
  payload,
}: {
  eventId: string;
  payload: ReportAlertPayload;
}) {
  const [data, setData] = useState<OcfReconciliationDrillResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    api
      .getFeedOcfReconciliation(eventId)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const metricItems: MetricItem[] = SUMMARY_KEYS.flatMap((key) => {
    const value = payload[key];
    if (typeof value !== "number") return [];
    return [{ label: pickMetricLabel(key), value: formatMetricValue(key, value) }];
  });

  const attachments = data ? collectAttachments(data.groups) : [];

  return (
    <InsightShell explainer="Agent-reported OCF vs the published rate for the same lane and equipment.">
      {metricItems.length > 0 && <MetricGrid items={metricItems} />}

      {attachments.length > 0 && (
        <div className="space-y-1">
          <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            Source file
          </p>
          <FeedAttachmentDownloads attachments={attachments} size="md" />
        </div>
      )}

      {loading && (
        <p className="text-[11px] text-muted-foreground">Loading mismatches…</p>
      )}
      {error && !loading && (
        <p className="text-[11px] text-muted-foreground">
          Couldn&apos;t load the mismatch detail.
        </p>
      )}

      {data &&
        SECTIONS.map(({ key, title }) => {
          const rows = data.groups[key] ?? [];
          if (rows.length === 0) return null;
          return (
            <EntitySection key={key} title={`${title} (${rows.length})`}>
              {rows.map((row) => (
                <DrillRow
                  key={`${row.source_id}:${row.equipment_size ?? ""}:${row.rate_line_id ?? ""}`}
                  row={row}
                />
              ))}
            </EntitySection>
          );
        })}
    </InsightShell>
  );
}
